// src/pages/access/UserGroupsTab.tsx -- Phase 5d.
//
// Newsletter distribution groups management surface, rendered as the "Groups"
// tab inside AccessUsersPage (already SSO-admin gated). Structurally cloned
// from src/pages/GroupManagerPage.tsx's list-edit + member-picker UX, but:
//   * points at /api/access/user-groups* (NOT the chat /api/access/groups)
//   * group shape { id, code, name, description, color, memberIds, memberCount }
//   * adds a colour picker (brass | heather | sage)
//   * labelled "Newsletter distribution groups" to distinguish it from the
//     chat Group Manager.
//
// These groups feed the CEO Newsletter distribution. DELETE returns 409
// { error:"group_in_use" } if the group is wired to a CEO edition.
//
// Endpoints (all SSO-admin, enforced server-side):
//   GET    /api/access/user-groups
//   POST   /api/access/user-groups            { name, description?, color }
//   PATCH  /api/access/user-groups/:id        { name?, description?, color? }
//   DELETE /api/access/user-groups/:id        (409 group_in_use)
//   GET    /api/access/user-groups/:id/members
//   PUT    /api/access/user-groups/:id/members { userIds:[...] }   (replace-all)
// The add-member picker reuses GET /api/access/users.

import { useCallback, useEffect, useMemo, useState } from "react";
import { apiFetch, ApiError } from "../../api";
import { C, F } from "../../theme";
import { Button, Card, Eyebrow, SearchInput } from "../../components/ui";

// ---------------------------------------------------------------------------
// Wire types
// ---------------------------------------------------------------------------

type GroupColor = "brass" | "heather" | "sage";

const COLOR_OPTIONS: ReadonlyArray<{ key: GroupColor; label: string }> = [
  { key: "brass", label: "Brass" },
  { key: "heather", label: "Heather" },
  { key: "sage", label: "Sage" },
];

interface UserGroup {
  id: number;
  code: string;
  name: string;
  description: string | null;
  color: GroupColor;
  memberIds: number[];
  memberCount: number;
  createdAt: string;
}

interface GroupMember {
  userId: number;
  firstName: string;
  lastName: string;
  email: string;
  addedAt: string;
}

interface AccessUserLite {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
}

// Colour token lookup for swatches/accents. Falls back to brass.
function colorTokens(color: GroupColor): { fill: string; bg: string; line: string; ink: string } {
  switch (color) {
    case "heather": return { fill: C.heather, bg: C.heatherBg, line: C.heatherLt, ink: C.heatherDk };
    case "sage":    return { fill: C.sage,    bg: C.sageBg,    line: C.sageLt,    ink: C.sageDk };
    case "brass":
    default:        return { fill: C.brass,   bg: C.brassBg,   line: C.brassLt,   ink: C.brassDk };
  }
}

function initials(name: string, code: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  if (parts.length === 1 && parts[0].length >= 2) return parts[0].slice(0, 2).toUpperCase();
  return (code.slice(0, 2) || "G").toUpperCase();
}

// ---------------------------------------------------------------------------
// Tab
// ---------------------------------------------------------------------------

export function UserGroupsTab() {
  const [groups, setGroups] = useState<UserGroup[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [creating, setCreating] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const loadGroups = useCallback(async () => {
    try {
      const r = await apiFetch<{ groups: UserGroup[] }>("/api/access/user-groups");
      setGroups(r.groups);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Couldn't load groups.");
    }
  }, []);

  useEffect(() => { void loadGroups(); }, [loadGroups]);

  useEffect(() => {
    if (!flash) return;
    const t = window.setTimeout(() => setFlash(null), 2600);
    return () => window.clearTimeout(t);
  }, [flash]);

  const filtered = useMemo(() => {
    if (!groups) return [];
    const q = search.trim().toLowerCase();
    if (!q) return groups;
    return groups.filter(
      (g) => g.name.toLowerCase().includes(q) || g.code.toLowerCase().includes(q),
    );
  }, [groups, search]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div>
        <Eyebrow c={C.heatherDk}>Newsletter distribution groups</Eyebrow>
        <div style={{ fontFamily: F.serif, fontStyle: "italic", fontSize: 12.5, color: C.inkMute, marginTop: 4, lineHeight: 1.45 }}>
          Board / leadership distribution lists for the CEO Newsletter. Separate from chat groups (Group Manager).
        </div>
      </div>

      {error && (
        <Card>
          <div style={{ color: C.signalDk, fontFamily: F.body, fontWeight: 700 }}>{error}</div>
        </Card>
      )}
      {flash && (
        <div role="status" style={{ padding: 12, borderRadius: 12, background: C.sageBg, border: `1px solid ${C.sageLt}`, color: C.sageDk, fontFamily: F.body, fontWeight: 700, fontSize: 13 }}>
          {flash}
        </div>
      )}

      {/* Toolbar */}
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <div style={{ flex: 1 }}>
          <SearchInput value={search} onChange={setSearch} placeholder="Search groups…" />
        </div>
        <Button variant="heather" onClick={() => setCreating((v) => !v)}>
          + New group
        </Button>
      </div>

      {creating && (
        <CreateGroupForm
          onCancel={() => setCreating(false)}
          onCreated={() => { setCreating(false); setFlash("Group created."); void loadGroups(); }}
          onError={setError}
        />
      )}

      {/* List */}
      {!groups && (
        <div style={{ height: 120, borderRadius: 16, background: C.paperWarm, border: `1px solid ${C.hairlineLt}` }} aria-hidden="true" />
      )}
      {groups && filtered.length === 0 && (
        <Card>
          <div style={{ padding: "16px 8px", textAlign: "center", fontFamily: F.serif, fontStyle: "italic", color: C.inkMute }}>
            {groups.length === 0 ? "No distribution groups yet. Create the first one above." : "No groups match your search."}
          </div>
        </Card>
      )}
      {filtered.map((g) => (
        <div key={g.id}>
          <GroupRow
            group={g}
            selected={selectedId === g.id}
            onToggle={() => setSelectedId((cur) => (cur === g.id ? null : g.id))}
          />
          {selectedId === g.id && (
            <GroupDetail
              group={g}
              onChanged={() => { setFlash("Saved."); void loadGroups(); }}
              onDeleted={() => { setSelectedId(null); setFlash("Group deleted."); void loadGroups(); }}
              onError={setError}
            />
          )}
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// List row
// ---------------------------------------------------------------------------

function GroupRow({ group, selected, onToggle }: { group: UserGroup; selected: boolean; onToggle: () => void }) {
  const tk = colorTokens(group.color);
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={selected}
      style={{
        width: "100%", textAlign: "left", display: "flex", alignItems: "center", gap: 12, padding: "12px 14px",
        background: C.card, border: `1px solid ${selected ? tk.ink : tk.line}`,
        borderRadius: selected ? "16px 16px 0 0" : 16, cursor: "pointer",
      }}
    >
      <span style={{
        width: 40, height: 40, borderRadius: 12, background: tk.fill,
        color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: F.serif,
        fontWeight: 700, fontSize: 13, flexShrink: 0,
      }}>
        {initials(group.name, group.code)}
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: "block", fontFamily: F.serif, fontSize: 16, fontWeight: 700, color: C.ink, letterSpacing: "-0.2px" }}>
          {group.name}
        </span>
        <span style={{ display: "block", fontFamily: F.mono, fontSize: 10.5, fontWeight: 700, letterSpacing: "0.6px", textTransform: "uppercase", color: C.inkMute, marginTop: 2 }}>
          {group.memberCount} member{group.memberCount === 1 ? "" : "s"} · {group.color}
        </span>
      </span>
      <span style={{ color: tk.ink, fontWeight: 800, fontSize: 14 }}>{selected ? "⌃" : "›"}</span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Detail — settings + membership
// ---------------------------------------------------------------------------

function GroupDetail({
  group, onChanged, onDeleted, onError,
}: {
  group: UserGroup;
  onChanged: () => void;
  onDeleted: () => void;
  onError: (msg: string) => void;
}) {
  const tk = colorTokens(group.color);
  const [name, setName] = useState(group.name);
  const [description, setDescription] = useState(group.description ?? "");
  const [color, setColor] = useState<GroupColor>(group.color);
  const [savingSettings, setSavingSettings] = useState(false);

  const [members, setMembers] = useState<GroupMember[] | null>(null);
  const [picker, setPicker] = useState(false);

  const loadMembers = useCallback(async () => {
    try {
      const r = await apiFetch<{ members: GroupMember[] }>(`/api/access/user-groups/${group.id}/members`);
      setMembers(r.members);
    } catch (e) {
      onError(e instanceof ApiError ? e.message : "Couldn't load members.");
    }
  }, [group.id, onError]);

  useEffect(() => { void loadMembers(); }, [loadMembers]);

  const saveSettings = async () => {
    if (name.trim().length === 0) { onError("Group name is required."); return; }
    setSavingSettings(true);
    try {
      await apiFetch(`/api/access/user-groups/${group.id}`, {
        method: "PATCH",
        body: JSON.stringify({
          name: name.trim(),
          description: description.trim() === "" ? null : description.trim(),
          color,
        }),
      });
      onChanged();
    } catch (e) {
      onError(e instanceof ApiError ? e.message : "Couldn't save settings.");
    } finally {
      setSavingSettings(false);
    }
  };

  const putMembers = async (userIds: number[]) => {
    try {
      await apiFetch(`/api/access/user-groups/${group.id}/members`, {
        method: "PUT",
        body: JSON.stringify({ userIds }),
      });
      await loadMembers();
      onChanged();
    } catch (e) {
      onError(e instanceof ApiError ? e.message : "Couldn't update members.");
    }
  };

  const removeMember = (userId: number) => {
    if (!members) return;
    void putMembers(members.filter((m) => m.userId !== userId).map((m) => m.userId));
  };

  const addMembers = (toAdd: number[]) => {
    if (!members) return;
    const next = Array.from(new Set([...members.map((m) => m.userId), ...toAdd]));
    setPicker(false);
    void putMembers(next);
  };

  const doDelete = async () => {
    if (!window.confirm(`Delete "${group.name}"? Members are detached; this can't be undone.`)) return;
    try {
      await apiFetch(`/api/access/user-groups/${group.id}`, { method: "DELETE" });
      onDeleted();
    } catch (e) {
      if (e instanceof ApiError && typeof e.body === "object" && e.body !== null &&
          (e.body as { error?: string }).error === "group_in_use") {
        onError("This group is on a CEO Newsletter edition and can't be deleted while in use.");
      } else {
        onError(e instanceof ApiError ? e.message : "Couldn't delete group.");
      }
    }
  };

  return (
    <div style={{ border: `1px solid ${tk.ink}`, borderTop: "none", borderRadius: "0 0 16px 16px", background: tk.bg, padding: "14px 14px 16px", display: "flex", flexDirection: "column", gap: 14 }}>
      {/* Members */}
      <div>
        <Label>Members</Label>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
          {members === null && <span style={{ fontFamily: F.body, fontSize: 12, color: C.inkMute }}>Loading…</span>}
          {members && members.length === 0 && <span style={{ fontFamily: F.serif, fontStyle: "italic", fontSize: 12.5, color: C.inkMute }}>No members yet.</span>}
          {members?.map((m) => (
            <span key={m.userId} style={{
              display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 6px 5px 10px",
              background: C.card, border: `1px solid ${C.hairline}`, borderRadius: 999,
              fontFamily: F.body, fontSize: 12.5, fontWeight: 700, color: C.ink,
            }}>
              {m.firstName} {m.lastName}
              <button
                type="button"
                aria-label={`Remove ${m.firstName} ${m.lastName}`}
                onClick={() => removeMember(m.userId)}
                style={{ width: 18, height: 18, borderRadius: 9, border: "none", background: tk.bg, color: tk.ink, cursor: "pointer", fontWeight: 800, lineHeight: 1, fontSize: 12 }}
              >
                ×
              </button>
            </span>
          ))}
          <button
            type="button"
            onClick={() => setPicker(true)}
            style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "5px 12px", background: tk.fill, color: "#fff", border: "none", borderRadius: 999, fontFamily: F.body, fontSize: 12.5, fontWeight: 800, cursor: "pointer" }}
          >
            + Add member
          </button>
        </div>
      </div>

      {/* Settings */}
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <Label>Settings</Label>
        <TextRow label="Name" value={name} onChange={setName} />
        <TextRow label="Description" value={description} onChange={setDescription} placeholder="Optional" />
        <div>
          <span style={{ display: "block", fontFamily: F.body, fontSize: 11, fontWeight: 700, color: C.inkSoft, marginBottom: 6 }}>Colour</span>
          <ColorPicker value={color} onChange={setColor} />
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 2 }}>
          <span style={{ fontFamily: F.mono, fontSize: 10, color: C.inkMute }}>code: {group.code} (locked)</span>
          <div style={{ flex: 1 }} />
          <Button variant="danger" onClick={doDelete}>Delete</Button>
          <Button variant="primary" onClick={saveSettings} disabled={savingSettings}>
            {savingSettings ? "Saving…" : "Save settings"}
          </Button>
        </div>
      </div>

      {picker && members && (
        <MemberPicker
          existingIds={new Set(members.map((m) => m.userId))}
          accent={tk}
          onCancel={() => setPicker(false)}
          onAdd={addMembers}
          onError={onError}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Create form
// ---------------------------------------------------------------------------

function CreateGroupForm({ onCancel, onCreated, onError }: { onCancel: () => void; onCreated: () => void; onError: (m: string) => void }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [color, setColor] = useState<GroupColor>("heather");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (name.trim().length === 0) { onError("Group name is required."); return; }
    setBusy(true);
    try {
      await apiFetch("/api/access/user-groups", {
        method: "POST",
        body: JSON.stringify({
          name: name.trim(),
          description: description.trim() === "" ? null : description.trim(),
          color,
        }),
      });
      onCreated();
    } catch (e) {
      onError(e instanceof ApiError ? e.message : "Couldn't create group.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ border: `1px solid ${C.heatherLt}`, background: C.card, borderRadius: 16, padding: 14, display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ fontFamily: F.serif, fontStyle: "italic", fontSize: 14, fontWeight: 700, color: C.heatherDk }}>New distribution group</div>
      <TextRow label="Name" value={name} onChange={setName} placeholder="e.g. Board of Trustees" />
      <TextRow label="Description" value={description} onChange={setDescription} placeholder="Optional" />
      <div>
        <span style={{ display: "block", fontFamily: F.body, fontSize: 11, fontWeight: 700, color: C.inkSoft, marginBottom: 6 }}>Colour</span>
        <ColorPicker value={color} onChange={setColor} />
      </div>
      <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
        <Button variant="secondary" fullWidth onClick={onCancel} disabled={busy}>Cancel</Button>
        <Button variant="primary" fullWidth onClick={submit} disabled={busy}>{busy ? "Creating…" : "Create group"}</Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Member picker — reuses GET /api/access/users
// ---------------------------------------------------------------------------

function MemberPicker({
  existingIds, accent, onCancel, onAdd, onError,
}: {
  existingIds: Set<number>;
  accent: { fill: string; bg: string; line: string; ink: string };
  onCancel: () => void;
  onAdd: (ids: number[]) => void;
  onError: (m: string) => void;
}) {
  const [users, setUsers] = useState<AccessUserLite[] | null>(null);
  const [search, setSearch] = useState("");
  const [picked, setPicked] = useState<Set<number>>(new Set());

  useEffect(() => {
    let cancelled = false;
    apiFetch<{ users: AccessUserLite[] }>("/api/access/users")
      .then((r) => { if (!cancelled) setUsers(r.users.filter((u) => !existingIds.has(u.id))); })
      .catch((e) => { if (!cancelled) onError(e instanceof ApiError ? e.message : "Couldn't load users."); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    if (!users) return [];
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => `${u.first_name} ${u.last_name} ${u.email}`.toLowerCase().includes(q));
  }, [users, search]);

  const toggle = (id: number) => setPicked((cur) => { const n = new Set(cur); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  return (
    <div role="dialog" aria-modal="true" aria-label="Add members" onClick={onCancel} style={{ position: "fixed", inset: 0, background: "rgba(43,32,20,0.34)", backdropFilter: "blur(2px)", display: "flex", alignItems: "flex-end", justifyContent: "center", zIndex: 220 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: 520, maxHeight: "80vh", background: C.paper, borderRadius: "22px 22px 0 0", padding: "16px 18px 20px", boxShadow: C.shadowL, display: "flex", flexDirection: "column" }}>
        <div style={{ width: 38, height: 4, borderRadius: 2, background: C.hairline, margin: "-6px auto 12px" }} />
        <div style={{ fontFamily: F.serif, fontStyle: "italic", fontSize: 15, fontWeight: 700, color: accent.ink, marginBottom: 10 }}>Add members</div>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search people…"
          aria-label="Search people"
          style={{ minHeight: 42, padding: "10px 12px", background: C.card, border: `1px solid ${C.hairline}`, borderRadius: 12, fontFamily: F.body, fontSize: 14, color: C.ink, outline: "none", marginBottom: 10, boxSizing: "border-box" }}
        />
        <div style={{ overflowY: "auto", flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
          {users === null && <span style={{ fontFamily: F.body, fontSize: 13, color: C.inkMute }}>Loading people…</span>}
          {users && filtered.length === 0 && <span style={{ fontFamily: F.serif, fontStyle: "italic", fontSize: 13, color: C.inkMute }}>No matching people.</span>}
          {filtered.map((u) => {
            const on = picked.has(u.id);
            return (
              <button key={u.id} type="button" onClick={() => toggle(u.id)} aria-pressed={on} style={{
                display: "flex", alignItems: "center", gap: 10, padding: "9px 12px", textAlign: "left",
                background: on ? accent.bg : C.card, border: `1.5px solid ${on ? accent.ink : C.hairline}`, borderRadius: 12, cursor: "pointer",
              }}>
                <span style={{ width: 22, height: 22, borderRadius: 6, border: `1.5px solid ${on ? accent.ink : C.hairline}`, background: on ? accent.fill : "transparent", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 800, flexShrink: 0 }}>{on ? "✓" : ""}</span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: "block", fontFamily: F.body, fontSize: 14, fontWeight: 700, color: C.ink }}>{u.first_name} {u.last_name}</span>
                  <span style={{ display: "block", fontFamily: F.mono, fontSize: 10.5, color: C.inkMute }}>{u.email}</span>
                </span>
              </button>
            );
          })}
        </div>
        <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
          <Button variant="secondary" fullWidth onClick={onCancel}>Cancel</Button>
          <Button variant="primary" fullWidth onClick={() => onAdd([...picked])} disabled={picked.size === 0}>
            Add {picked.size > 0 ? `(${picked.size})` : ""}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Small shared inputs
// ---------------------------------------------------------------------------

function ColorPicker({ value, onChange }: { value: GroupColor; onChange: (c: GroupColor) => void }) {
  return (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
      {COLOR_OPTIONS.map((opt) => {
        const tk = colorTokens(opt.key);
        const on = value === opt.key;
        return (
          <button
            key={opt.key}
            type="button"
            onClick={() => onChange(opt.key)}
            aria-pressed={on}
            style={{
              display: "inline-flex", alignItems: "center", gap: 8, padding: "7px 12px", borderRadius: 999,
              border: on ? `1.5px solid ${tk.ink}` : `1px solid ${C.hairline}`,
              background: on ? tk.bg : C.card, color: on ? tk.ink : C.inkSoft,
              fontFamily: F.body, fontSize: 12.5, fontWeight: 800, cursor: "pointer",
            }}
          >
            <span style={{ width: 14, height: 14, borderRadius: "50%", background: tk.fill, flexShrink: 0 }} />
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return <div style={{ fontFamily: F.body, fontSize: 10.5, fontWeight: 800, letterSpacing: "1.2px", textTransform: "uppercase", color: C.inkMute }}>{children}</div>;
}

function TextRow({ label, value, onChange, placeholder }: { label: string; value: string; onChange: (v: string) => void; placeholder?: string }) {
  return (
    <label style={{ display: "block" }}>
      <span style={{ display: "block", fontFamily: F.body, fontSize: 11, fontWeight: 700, color: C.inkSoft, marginBottom: 4 }}>{label}</span>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        style={{ width: "100%", minHeight: 40, padding: "9px 12px", background: C.card, border: `1px solid ${C.hairline}`, borderRadius: 10, fontFamily: F.body, fontSize: 14, color: C.ink, outline: "none", boxSizing: "border-box" }}
      />
    </label>
  );
}
